const countriesEn = [
	{
		slug: 'dubai',
		flag: '🇦🇪',
		name: 'Dubai / UAE',
		subtitle: 'A tax-free future in the desert',
		taxRate: '0%',
		taxLabel: 'Tax on wealth',
		pros: ['No income tax', 'No wealth tax', 'No Box 3', 'Top lifestyle'],
		cons: ['40°C in summer', 'Far from family', 'Alcohol is expensive', 'Strict laws'],
		color: '#c8960c',
		bgColor: '#fff8e1',
		content: {
			intro: `Dubai and the United Arab Emirates have become one of the most popular destinations for Dutch high-net-worth individuals who want to live tax-free. With no income, wealth, or dividend tax, the fiscal appeal is enormous — but daily life in the desert requires a deliberate choice.`,
			sections: [
				{
					title: 'Tax benefits for Dutch nationals',
					body: `The UAE levies no income tax, no wealth tax, and no capital gains tax. For a Dutch person looking to escape Box 3, this is the most radical alternative: instead of 36% on returns, you simply pay nothing. After emigration, the Netherlands may still impose exit tax on certain substantial shareholdings for up to 10 years — but this does not apply to ordinary investment portfolios. Consult an expat tax advisor for your specific situation.`,
				},
				{
					title: 'Dubai Retirement Visa',
					body: `Since 2020, Dubai has offered an official Retirement Visa for people aged 55 and over. Requirements: at least €1 million in UAE real estate, or a savings balance of around €250,000, or a monthly income of at least €5,000. The visa is valid for five years and renewable. There is no minimum residency requirement, but fiscal residency generally requires 183 days of presence per year.`,
				},
				{
					title: 'Cost of living',
					body: `Dubai is more expensive than many people expect. An apartment in a desirable neighbourhood (Dubai Marina, Downtown, JBR) quickly costs €2,000–€4,000 per month. Food, restaurants, and entertainment are comparable to Amsterdam. Alcohol is only available in hotels and licensed shops — and heavily taxed. Driving is essential; public transport is limited outside the metro.`,
				},
				{
					title: 'Practical considerations',
					body: `Summer (June–September) with temperatures of 40–48°C and high humidity is virtually uninhabitable outdoors. Many expats leave for three months a year. The Dutch-speaking community is present but small. Healthcare is of a high standard in the cities, but private insurance is mandatory. Family law and social norms differ significantly from Dutch standards.`,
				},
			],
			cta: 'Want to know what a fiscal move to Dubai means for your portfolio?',
			advisorText: 'Request a free orientation consultation with a specialist expat tax advisor.',
		},
	},
	{
		slug: 'portugal',
		flag: '🇵🇹',
		name: 'Portugal',
		subtitle: 'Sun, sea and lower taxes',
		taxRate: '10%',
		taxLabel: 'Flat rate (NHR scheme)',
		pros: ['Stable climate', 'Low cost of living', 'English-speaking', 'EU member'],
		cons: ['NHR phased out for new applicants', 'Bureaucracy', 'Low local wages'],
		color: '#006b3c',
		bgColor: '#e8f5e9',
		content: {
			intro: `Portugal has long been the number one destination for early-retired Dutch nationals — and although the NHR regime has since changed, Portugal remains one of the most attractive EU countries for fiscal emigrants. The combination of a Mediterranean climate, affordable living (outside Lisbon), safety, and EU membership is unique.`,
			sections: [
				{
					title: 'IFICI scheme: the successor to NHR',
					body: `The original NHR (Non-Habitual Resident) regime ended for new applicants in 2024. The successor, IFICI (Incentivo Fiscal à Investigação Científica e Inovação), targets primarily researchers, highly skilled workers in innovative sectors, and start-ups. The classic NHR 10% flat rate on pension income is no longer automatically available for regular retirees. However, the "Decreto-Lei" scheme allows foreign pension income to benefit from tax advantages under certain conditions. Consult a Portuguese tax specialist for the current situation.`,
				},
				{
					title: 'D7 Visa — for early retirees',
					body: `As an EU citizen you do not need a visa to live in Portugal. You simply obtain a NIF (tax number), open a bank account, and register with the municipality. Within five years you can apply for permanent residency. The D7 visa is intended for non-EU citizens with passive income — as a Dutch national this is not relevant.`,
				},
				{
					title: 'Cost of living',
					body: `Outside Lisbon and Porto, Portugal is still very affordable. The Algarve (Faro, Lagos, Tavira) offers excellent quality of life for €1,500–€2,500/month for a couple. Buying or renting property is cheaper than in the Netherlands, although prices in tourist zones have risen considerably. Groceries, restaurants, and healthcare are significantly cheaper than in the Netherlands.`,
				},
				{
					title: 'Life as a Dutch person in Portugal',
					body: `The Dutch-speaking community in the Algarve is large — you can function entirely in English or even Dutch. Learning Portuguese helps enormously for bureaucratic matters. Healthcare is of reasonable quality; private insurance is recommended. Portugal is politically stable, safe, and has excellent flight connections to the Netherlands (Eindhoven, Amsterdam → Faro/Lisbon).`,
				},
			],
			cta: 'Curious how much you save by emigrating to Portugal?',
			advisorText: 'Have a Portuguese tax specialist calculate your situation.',
		},
	},
	{
		slug: 'singapore',
		flag: '🇸🇬',
		name: 'Singapore',
		subtitle: 'Asian financial hub',
		taxRate: '0%',
		taxLabel: 'Capital gains tax',
		pros: ['No CGT', 'Stable economy', 'Safe', 'Excellent healthcare'],
		cons: ['Sky-high rents', '32°C + 90% humidity', 'Far away', 'Chewing gum banned'],
		color: '#c8102e',
		bgColor: '#fce4ec',
		content: {
			intro: `Singapore is the financial centre of Southeast Asia and attracts wealthy expats with its zero capital gains tax, political stability, and world-class healthcare and education. However, it is not cheap — and the climate is tropical year-round.`,
			sections: [
				{
					title: 'Tax advantage',
					body: `Singapore levies no tax on capital gains, dividends, or foreign income not remitted to Singapore. Income tax only applies to locally earned income and is progressive up to 22%. For someone with a passive investment portfolio who lives in Singapore off their wealth, the effective tax burden is essentially zero.`,
				},
				{
					title: 'Residency permit',
					body: `Permanent Residence (PR) or an Employment Pass is required for long-term stay. The Global Investor Programme (GIP) offers PR to wealthy individuals who invest at least SGD 10 million (approximately €7 million) in Singapore. This makes Singapore largely inaccessible to the average FIRE seeker. Alternatives include the EntrePass (for entrepreneurs) or an Employment Pass through your own BV/entity.`,
				},
				{
					title: 'Cost of living',
					body: `Singapore is one of the most expensive cities in the world. Rent for expat-suitable accommodation: SGD 4,000–10,000/month (€2,800–€7,000). Cars are taxed so heavily that a Toyota Corolla costs €80,000+. Eating at hawker centres (local food courts) is very affordable. Healthcare is excellent and accessible without local insurance, but expensive.`,
				},
				{
					title: 'Life in Singapore',
					body: `Singapore is extremely safe, clean, and efficient. Public transport is world-class. English is the working language. The climate — 32°C and 80–90% humidity all year — is bliss for some, exhausting for others. There is no significant Dutch-speaking community, but the international expat community is large.`,
				},
			],
			cta: 'Singapore as a financial haven — is it realistic for you?',
			advisorText: 'Discuss the possibilities with an international tax advisor.',
		},
	},
	{
		slug: 'cyprus',
		flag: '🇨🇾',
		name: 'Cyprus',
		subtitle: 'EU island with tax advantages',
		taxRate: '0%',
		taxLabel: 'Tax on share gains',
		pros: ['No CGT on shares', 'EU member', 'Mediterranean climate', 'Low cost of living'],
		cons: ['Small economy', 'Occasional political tensions', 'Past banking crisis'],
		color: '#006b5b',
		bgColor: '#e0f2f1',
		content: {
			intro: `Cyprus is an EU island in the eastern Mediterranean with attractive tax rules, a warm climate, and lower costs than Western Europe. For equity investors, the zero percent capital gains tax is the biggest draw.`,
			sections: [
				{
					title: 'Zero percent on share gains',
					body: `Cyprus levies no tax on capital gains from shares. Dividends and interest income are taxed (via Special Defence Contribution), but for someone holding and selling ETFs or growth shares, the tax saving is enormous. Income tax is progressive from 0% to 35%, but with Non-Domiciled status, foreign dividend and interest income is exempt for up to 17 years.`,
				},
				{
					title: 'Non-Domiciled status',
					body: `If you apply for Non-Dom status — available to new tax residents who have no Cypriot "domicile of origin" — you pay no tax on foreign dividends and interest for 17 years. This is particularly attractive for investors with a large portfolio that pays annual dividends.`,
				},
				{
					title: 'Cost of living',
					body: `Cyprus is considerably cheaper than the Netherlands. Renting in Limassol (the most popular expat city): €800–€2,000/month for a good apartment. Food and drink are affordable. The climate is excellent: 300+ sunny days per year, mild winters. Limassol has a growing international community and modern amenities.`,
				},
				{
					title: 'Practical information',
					body: `As an EU citizen you have free movement rights in Cyprus. English is very widely spoken — the island was British until 1960. The legal system is based on English Common Law, which makes it more familiar to Dutch nationals than Romance legal systems. The 2013 banking crisis is history but left reputational damage; Dutch media tend to be more critical of Cypriot banks than the reality warrants.`,
				},
			],
			cta: 'How much do you save on capital gains by emigrating to Cyprus?',
			advisorText: 'Request a free calculation from a Cypriot tax advisor.',
		},
	},
	{
		slug: 'monaco',
		flag: '🇲🇨',
		name: 'Monaco',
		subtitle: 'For when things are really going well',
		taxRate: '0%',
		taxLabel: 'Income tax',
		pros: ['No income tax', 'Ultimate prestige', "World's safest country", 'F1 on your doorstep'],
		cons: ['Rent: €10,000+/month', 'You need to be a millionaire', 'Very small', 'Not for ordinary people'],
		color: '#a6192e',
		bgColor: '#fce4ec',
		content: {
			intro: `Monaco is the smallest independent country in Europe and at the same time the most exclusive fiscal haven in the Western world. No income tax, no wealth tax — but also no affordable housing. Monaco is for a very select group.`,
			sections: [
				{
					title: 'Tax regime',
					body: `Monaco levies no income tax on non-French residents. No wealth tax, no inheritance tax for immediate family members, no capital gains tax. French citizens are excluded from this (tax treaty with France). For Dutch nationals, the full benefit applies. There is 20% VAT, the same as in the EU.`,
				},
				{
					title: 'Residency requirements',
					body: `You must demonstrate sufficient financial means (no minimum amount set but €500,000+ is a realistic threshold), own or rent a property in Monaco, and open a bank account at a Monegasque bank. After three months you receive the "carte de résident." There is no tax threshold for residence days — you do not need to be present 183 days a year.`,
				},
				{
					title: 'Cost of living',
					body: `Monaco is the most expensive square metre in the world. Rent: €10,000–€50,000+/month for an apartment. Buying: €50,000–€100,000+ per m². Restaurants are very expensive. The costs are offset by the absence of tax — but you need at least €5–10 million in assets to do this comfortably.`,
				},
				{
					title: 'Life in Monaco',
					body: `Monaco is 2.02 km² — smaller than Vondelpark and its surroundings. It is extremely safe (more surveillance cameras per m² than any other country), has world-class restaurants and shops, and an international, multilingual network of ultra-high-net-worth individuals. The Formula 1 Grand Prix literally races through the streets. Ideal for those who seek privacy, security, and status — not for those who want space and tranquillity.`,
				},
			],
			cta: 'Monaco is not for everyone — but maybe for you?',
			advisorText: 'Have an international wealth advisor assess the feasibility for your situation.',
		},
	},
	{
		slug: 'zwitserland',
		flag: '🇨🇭',
		name: 'Switzerland',
		subtitle: 'Lump-sum taxation for the wealthy',
		taxRate: 'Forfait',
		taxLabel: 'Based on cost of living',
		pros: ['High standard of living', 'Political stability', 'No federal CGT', 'Stunning nature'],
		cons: ['Very expensive', 'Hard to immigrate', 'Not in the EU', 'Learn German/French/Italian'],
		color: '#d52b1e',
		bgColor: '#ffebee',
		content: {
			intro: `Switzerland is Europe's most stable country and has a unique tax system: the lump sum regime for wealthy foreigners. You pay tax on a notional income based on your cost of living — not on your actual wealth or income.`,
			sections: [
				{
					title: 'Lump sum taxation',
					body: `The Swiss "Pauschalbesteuerung" (lump sum) regime allows foreigners who do not work in Switzerland to pay tax on a notional income — generally five times the annual rental value of their property. For someone renting a villa for €5,000/month, the taxable income is therefore €300,000/year. On €5 million of investment income, the effective percentage is very low. Available in the cantons of Berne, Valais, Vaud, Geneva, Ticino, and others.`,
				},
				{
					title: 'No federal CGT',
					body: `At the federal level, Switzerland levies no capital gains tax on shares for private individuals who are not considered professional traders. This varies at the cantonal level. Dividends are subject to withholding tax (35%), but a refund is possible through the NL-CH tax treaty.`,
				},
				{
					title: 'Immigration requirements',
					body: `As an EU citizen you may live in Switzerland (Free Movement of Persons Agreement CH-EU). You need a B permit and must demonstrate you are financially self-sufficient. The application for the lump sum regime is handled by the cantonal tax authority. The threshold varies by canton but is generally at least CHF 400,000 in notional taxable income.`,
				},
				{
					title: 'Cost of living',
					body: `Switzerland is the most expensive European country after Monaco. Groceries are 2–3 times more expensive than in the Netherlands. Rent in Geneva or Zürich: €3,000–€8,000/month. The nature, tranquillity, reliability, and quality of life are, however, exceptional. Switzerland is ideal for those seeking Europe's best quality of life and willing to pay for it.`,
				},
			],
			cta: 'Is the Swiss lump sum regime right for you?',
			advisorText: 'A Swiss tax specialist can calculate the minimum tax threshold for your situation.',
		},
	},
	{
		slug: 'thailand',
		flag: '🇹🇭',
		name: 'Thailand',
		subtitle: 'Tropical living at a fraction of the price',
		taxRate: '0%',
		taxLabel: 'On foreign wealth (LTR visa)',
		pros: ['Very low cost of living', 'Warm and sunny', 'LTR visa for the wealthy', 'Amazing food'],
		cons: ['Not an EU member', 'Language barrier', 'Limited healthcare outside cities', 'Political instability'],
		color: '#a51931',
		bgColor: '#fce4ec',
		content: {
			intro: `Thailand is the go-to destination for the cost-conscious FIRE seeker. With a lifestyle achievable in Chiang Mai for €800–€1,200 per month, a tropical climate, and the new LTR visa offering fiscal exemption on foreign income, Thailand is seriously on the radar for early retirees.`,
			sections: [
				{
					title: 'LTR Visa (Long Term Resident)',
					body: `Since 2022, Thailand has offered the LTR visa for "Wealthy Pensioners": those aged 50+ with at least $80,000 (approximately €73,000) in passive income per year, or assets of at least $250,000 plus supplementary health insurance. The visa grants 10-year residency rights with the possibility of bringing 4 friends/family members. Tax: income earned before arrival in Thailand that is remitted later is tax-free in Thailand.`,
				},
				{
					title: 'Taxation in Thailand',
					body: `Thailand only taxes income remitted to Thailand in the same calendar year it was earned. Capital gains from investments outside Thailand are effectively untaxed if structured correctly. With an LTR visa, a flat rate of 17% applies to local income — but for retired investors, local income is typically zero.`,
				},
				{
					title: 'Cost of living',
					body: `Chiang Mai is the most popular expat destination: rent for a good apartment €300–€600/month, food €5–€15/day in local restaurants. Bangkok is more expensive but still affordable: €600–€1,500/month rent for a Western standard. Phuket and Koh Samui are more expensive and more touristy. The average FIRE seeker achieves a comfortable lifestyle for €1,000–€1,800/month.`,
				},
				{
					title: 'Practical considerations',
					body: `Learning Thai is difficult, but friendliness and English are widespread in expat areas. Healthcare in Bangkok and Chiang Mai is of excellent international quality — private hospitals are affordable. Outside the cities, this drops off quickly. Political instability (coups) is historically present but generally does not directly affect expats. Summer heat (March–May) can reach extreme temperatures (40°C+).`,
				},
			],
			cta: 'Thailand at €1,200/month — does your calculation match this scenario?',
			advisorText: 'Calculate your scenario with lower monthly expenses in the calculator above.',
		},
	},
	{
		slug: 'spanje',
		flag: '🇪🇸',
		name: 'Spain',
		subtitle: 'Sun, tapas and the Beckham Law',
		taxRate: '0%',
		taxLabel: 'On foreign wealth (Beckham Law)',
		pros: ['0% on foreign investments (6 years)', 'Close to the Netherlands', 'EU member', 'Wonderful climate'],
		cons: [
			'After 6 years 19–28% (still < Box 3)',
			'Spanish shares/interest are taxed',
			'Bureaucracy',
			'Rents in Barcelona/Madrid have risen',
		],
		color: '#c60b1e',
		bgColor: '#ffebee',
		content: {
			intro: `Spain is the most popular emigration destination for Dutch nationals — close to home, EU member, wonderful climate, and a large Dutch community along the Costas. The Beckham Law also makes it fiscally attractive for investors: six years of 0% on foreign investment income.`,
			sections: [
				{
					title: 'Beckham Law explained',
					body: `The Beckham Law (officially: Régimen Especial para Trabajadores Desplazados, art. 93 IRPF) states that new Spanish tax residents can, for six years, opt for a flat rate of 24% on Spanish income and 0% on foreign income. For a Dutch national with an ETF portfolio at DEGIRO or a foreign bank, this means: six years of no Spanish tax on dividends, capital gains, or interest from that portfolio. After six years, regular Spanish rates apply (19–28% on savings/wealth — still lower than Dutch Box 3 at higher returns).`,
				},
				{
					title: 'Applying for the Beckham Law',
					body: `You apply for the Beckham Law through a Spanish gestor (tax advisor) using form Modelo 149, within six months of obtaining fiscal residency. Requirement: you must not have been a Spanish tax resident in the past five years. Tip: find a gestor experienced with foreigners (not all Spanish tax advisors know the regime well).`,
				},
				{
					title: 'Best regions for Dutch retirees',
					body: `Costa Blanca (Alicante, Torrevieja, Jávea) is the most popular region for Dutch nationals: warm climate, affordable (even after recent increases), large Dutch-speaking community, good flight connections (Eindhoven–Alicante). Costa del Sol (Málaga, Marbella, Estepona) is more luxurious and expensive. Canary Islands (Tenerife, Gran Canaria) offer the most stable year-round climate. Barcelona and Madrid are beautiful but expensive.`,
				},
				{
					title: 'Healthcare and practical life',
					body: `As an EU citizen you are entitled to Spanish healthcare once you become a fiscal resident and register with the municipality (empadronamiento). The quality of the public system varies — good in major cities, waiting times in rural areas. Private insurance (€50–€150/month) is recommended for comfort and speed. Learning Spanish helps enormously with bureaucracy, but in many expat areas you can get by with English and Dutch.`,
				},
			],
			cta: 'Calculate what the Beckham Law means for your portfolio',
			advisorText: 'A specialist Spanish-Dutch tax advisor can calculate the exact savings for you.',
		},
	},
];

export default countriesEn;
